import moment from 'moment';
import {TenantsCommunityTotalDTO} from '@rsp/models/dto/tenants-community-totals.dto';
import {defer, interval, Observable, Subject} from 'rxjs';
import {debounce, filter, finalize, switchMap, tap} from 'rxjs/operators';
import {ChartState} from '@rsp/models/chart.state';
import {HttpResult} from '@rsp/shared/services/rest/api-http.service';

export interface DateRange {
  start: Date;
  end: Date;
}

// ------------- Date (start) --------------

// Get local date time and returns UTC
export function dateToZonedDateTime(date: Date): string {
  const nDate = new Date(date);
  return toUTC(nDate).toISOString().substring(0, 10);
}

export function getNow(): Date {
  return new Date();
}

export function toUTC(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

export function getUTCNow(): Date {
  const localNow = getNow();
  return toUTC(localNow);
}

export function isDateInsideRange(date: Date, start: Date, end: Date): boolean {
  return start.getTime() <= date.getTime() && date.getTime() <= end.getTime();
}

export function getCalanderWeek(date: Date): number {
  return moment(date).week();
}

export function getStartOfWeek(date: Date): Date {
  const d = moment(date).startOf('week').add(1, 'days').toDate();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0));
}

export function getStartOfDay(date: Date): Date {
  const d = new Date(date);
  d.setUTCHours(0);
  d.setUTCMinutes(0);
  d.setUTCSeconds(0);
  d.setUTCMilliseconds(0);
  return d;
}

export function getEndOfWeek(date: Date): Date {
  const d = getStartOfWeek(date);
  d.setDate(d.getDate() + 6);

  return d;
}

export function getEndOfDay(date: Date): Date {
  const d = new Date(date);
  d.setUTCHours(23);
  d.setUTCMinutes(59);
  d.setUTCSeconds(59);
  return d;
}

export function getStartOfMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

export function getEndOfMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));
}

export function getStartOfYear(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
}

export function getEndOfYear(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear() + 1, 0, 0));
}

// ------------- Date (end) --------------


export function getStyleColor(name: string): string {
  const element = document.querySelector(':root');
  return window.getComputedStyle(element).getPropertyValue(name);
}


export function sumConsumption(dto: TenantsCommunityTotalDTO): number {
  return dto !== undefined ? dto.bioConsumption + dto.waterConsumption + dto.windConsumption + dto.pvConsumption : 0;
}

export function sumProduction(dto: TenantsCommunityTotalDTO): number {
  return dto !== undefined ? dto.bioProduction + dto.waterProduction + dto.windProduction + dto.pvProduction : 0;

}


export function prepare<T>(callback: () => void): (source: Observable<T>) => Observable<T> {
  return (source: Observable<T>): Observable<T> => defer(() => {
    callback();
    return source;
  });
}

export function indicate<T>(indicator: Subject<boolean>): (source: Observable<T>) => Observable<T> {
  return (source: Observable<T>): Observable<T> => source.pipe(
    prepare(() => indicator.next(true)),
    finalize(() => indicator.next(false))
  );
}

export function throttleRequest<T>(method: (a: any) => Observable<T>, chartState: ChartState): (source: Observable<T>) => Observable<T> {
  return (source: Observable<T>): Observable<T> => source.pipe(
    debounce(() => interval(200)),
    tap(() => {
      chartState.isLoading = true;
    }),
    switchMap((param) => {
      if (param) {
        return (<Observable<T>>method(param));
      }

      return new Observable<T>((ob) => {
        ob.complete();
      });
    }),
    tap(() => {
      chartState.isLoading = false;
    }),
  );
}

export function validateError<T>(chartState: ChartState): (source: Observable<HttpResult<T>>) => Observable<HttpResult<T>> {
  return (source: Observable<HttpResult<T>>): Observable<HttpResult<T>> => source.pipe(
    tap(() => chartState.hasError = true),

    filter((val) => {
      if (val.error !== undefined) {
        return false;
      }

      chartState.hasError = false;
      return true;
    })
  );
}

export function validateContent<T>(method: (dto: T) => boolean, chartState: ChartState):
  (source: Observable<HttpResult<T>>) => Observable<HttpResult<T>> {
  return (source: Observable<HttpResult<T>>): Observable<HttpResult<T>> => source.pipe(
    tap(() => chartState.hasNoData = true),

    filter((val) => {
      if (!method(val.result)) {
        return false;
      }

      chartState.hasNoData = false;
      return true;
    })
  );
}


export function validateChain<T>(method: any, chartState): (source: Observable<HttpResult<T>>) => Observable<HttpResult<T>> {
  return (source: Observable<HttpResult<T>>): Observable<HttpResult<T>> => source.pipe(
    validateError(chartState),
    validateContent((dto) => method(dto), chartState)
  );
}
